import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowRight, KeyRound, CheckCircle2 } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_cercle-local-bhealth/artifacts/csfd9ojj_logofinal-logo-full-color-rgb-900px-w-72ppi.png";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const kind = params.get("kind") || "admin";
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 8) { toast.error("Au moins 8 caractères"); return; }
    if (pwd !== pwd2) { toast.error("Les mots de passe ne correspondent pas"); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pwd });
      setDone(true);
      toast.success("Mot de passe mis à jour");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Lien invalide ou expiré");
    } finally { setBusy(false); }
  };

  const loginPath = kind === "merchant" ? "/partenaire/login" : "/admin/login";

  if (!token) {
    return (
      <Wrap>
        <h2 className="font-heading font-extrabold text-3xl text-navy-900">Lien invalide</h2>
        <p className="text-muted-foreground mt-2">Veuillez refaire une demande de réinitialisation.</p>
        <Link to={loginPath} className="btn-navy mt-6">Retour à la connexion <ArrowRight className="w-4 h-4" /></Link>
      </Wrap>
    );
  }

  if (done) {
    return (
      <Wrap>
        <div className="w-16 h-16 rounded-full bg-lime-500 mx-auto flex items-center justify-center mb-5">
          <CheckCircle2 className="w-8 h-8 text-navy-900" />
        </div>
        <h2 className="font-heading font-extrabold text-3xl text-navy-900 text-center">Mot de passe mis à jour</h2>
        <p className="text-muted-foreground mt-2 text-center">Vous pouvez maintenant vous connecter.</p>
        <button onClick={() => nav(loginPath)} className="btn-navy mt-6 w-full" data-testid="reset-back-login">Se connecter <ArrowRight className="w-4 h-4" /></button>
      </Wrap>
    );
  }

  return (
    <Wrap>
      <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-navy-900/60 font-semibold mb-3">
        <KeyRound className="w-3.5 h-3.5" /> Nouveau mot de passe
      </div>
      <h2 className="font-heading font-extrabold text-3xl text-navy-900 tracking-tighter">Choisissez votre mot de passe</h2>
      <p className="mt-2 text-muted-foreground text-sm">8 caractères minimum.</p>
      <form onSubmit={submit} className="mt-6 space-y-4" data-testid="reset-form">
        <div>
          <Label className="text-xs uppercase tracking-wider font-semibold text-navy-900/70">Nouveau mot de passe</Label>
          <Input data-testid="reset-password" type="password" required minLength={8} value={pwd} onChange={(e) => setPwd(e.target.value)} className="mt-1.5 h-12" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider font-semibold text-navy-900/70">Confirmer</Label>
          <Input data-testid="reset-password-confirm" type="password" required value={pwd2} onChange={(e) => setPwd2(e.target.value)} className="mt-1.5 h-12" />
        </div>
        <button type="submit" disabled={busy} className="btn-navy w-full h-12" data-testid="reset-submit">
          {busy ? "Mise à jour…" : "Valider le nouveau mot de passe"} <ArrowRight className="w-4 h-4" />
        </button>
      </form>
    </Wrap>
  );
}

function Wrap({ children }) {
  return (
    <div className="min-h-screen grid place-items-center bg-muted p-6">
      <div className="w-full max-w-md bg-white rounded-3xl shadow-[0_20px_50px_-15px_rgba(11,18,32,0.15)] p-8 md:p-10">
        <Link to="/" className="flex items-center gap-3 mb-8">
          <img src={LOGO_URL} alt="B.health®" className="h-10 w-10 object-contain" />
          <div className="font-heading font-extrabold uppercase tracking-wider text-sm text-navy-900">Local Benefits</div>
        </Link>
        {children}
      </div>
    </div>
  );
}
